import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import compareDesc from 'date-fns/compareDesc';
import ComponentView from 'common/ComponentView';
import InformationMessage from 'components/InformationMessage';
import RenderLink from 'components/RenderLink';
import OrangeInfoBanner from 'components/InformationBanner/OrangeInfoBanner';
import * as DefaultText from 'configuration/defaultText';
import AdvertorialPanel from 'panels/AdvertorialPanel';
import DocumentsListing from 'panels/DocumentsListing';
import trackEvent from 'services/AnalyticsService';
import { GetContent, GetTileContent, MapAdvertPanel } from 'services/ContentService';
import { ResponseCode } from 'services/models/httpResponse';
import PageTitle from 'shared/components/PageTitle/PageTitle';
import { filterComponents } from 'utils/ContentHelpers';
import { loggerFunc } from 'utils/logger';
import * as RouteConstants from 'constants/routeConstants';
import { Link } from 'react-router-dom';
import InfoBlock from 'components/InfoBlock';
import useUserProducts from 'utils/useUserProducts';
import { DOCUMENT_TYPE_BOOKLET } from '../../constants/documentConstants';
import AbstractGrid from './MembershipOverview/GridLayout/AbstractGrid';
import TileSets from '../../utils/tileSets.enum';
import { useApplicationState } from '../../hooks/applicationState-Context';
import { RENEWAL } from '../../constants/routeConstants';

const logger = loggerFunc('MembershipDocuments');

export const MembershipDocuments = () => {
  const visitedRenewalPage = useSelector((state) => state?.content?.flags?.visitedRenewalPage);
  const { [TileSets.documentsPageAds]: documentTiles } = useSelector((state) => state.content.tiles);
  const { Id: customerId, hasError: customerHasError } = useSelector((state) => state.customer.custInfo);
  const policyHasError = useSelector((state) => state.policy.policyInfo.hasError);
  const enableCarInsuranceWeb = useSelector((state) => state.featureToggle?.enableCarInsuranceWeb);
  const { hasCarInsurancePolicy } = useUserProducts()
  const { memberNo, renewBannerShown, renewBannerImgUrl } = useSelector((state) => state.session);
  const documents = useSelector((state) => state.documents);
  const [pageModel, setPageModel] = useState();
  const [errorText, setErrorText] = useState();
  const [adobeTag, setAdobeTag] = useState();
  const [splitDocuments, setSplitDocuments] = useState({});
  const { appState, setPageLoading } = useApplicationState();

  const renewalBannerAnalytics = () => {
    // Split image url into array
    const urlPath = renewBannerImgUrl.split('/');
    // Get last item from array and slice off file extension
    const filename = urlPath[urlPath.length - 1].slice(0, -4);
    if (filename) {
      // Event fired on page load
      trackEvent(`MyRAC_Documents_${filename}_Shown`);
      // Event set in state and fired through RenderLink action
      setAdobeTag(`MyRAC_Documents_${filename}_Clicked`);
    }
  };

  const processPageModelContent = (result) => {
    if (result.responseCode === ResponseCode.ERROR) {
      setPageModel({ error: DefaultText.APIError });
      return;
    }

    const membershipDocument = filterComponents('membershipdocumentcomponent', result.payload);

    const pageData = {
      title: membershipDocument.title,
      advertorial: MapAdvertPanel(result.payload),
      documentCMS: {
        subheading: membershipDocument.subheading,
        documentIcon: {
          icon: membershipDocument.documenticon.url,
          title: membershipDocument.documenticon.title,
        },
        downloadIcon: {
          icon: membershipDocument.downloadimage.url,
          title: membershipDocument.downloadimage.title,
        },
        downloadlinktext: membershipDocument.downloadlinktext,
      },
    };

    setPageModel(pageData);
  };

  const orderDocuments = (documentsArray) => documentsArray.sort((a, b) => {
    const result = compareDesc(new Date(a.DocumentDate), new Date(b.DocumentDate));
    return result;
  });

  const extractLatestBooklet = (documentsArray) => {
    for (let i = 0; i < documentsArray.length; i++) {
      if (documentsArray[i].DocumentType === DOCUMENT_TYPE_BOOKLET) {
        return documentsArray.splice(i, 1)[0];
      }
    }
    return null;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setupDocuments = (localDocuments) => {
    const orderedDocuments = orderDocuments(localDocuments);
    const booklet = extractLatestBooklet(orderedDocuments);

    const latestDocuments = orderedDocuments.splice(0, 2);
    if (booklet) {
      latestDocuments.push(booklet);
    }
    return { currentDocuments: latestDocuments, olderDocuments: orderedDocuments };
  };

  const handleError = (e, text) => {
    setErrorText(text);
    logger.error(text, e);
  };

  useEffect(() => {
    async function fetchContentData() {
      try {
        const pageData = await GetContent('loggedinpages', 'Membership documents', ['componentitems']);
        processPageModelContent(pageData);
      } catch (e) {
        handleError(e);
      }
    }

    if (!pageModel && customerId) {
      fetchContentData().then(() => {
        GetTileContent(customerId, memberNo, TileSets.documentsPageAds, false).then(setPageLoading(false))
          .catch((e) => {
            setPageLoading(false);
            handleError(e);
          });
      })
        .catch((e) => {
          setPageLoading(false);
          handleError(e);
        });
    }

    // Analytics
    if (renewBannerImgUrl) {
      renewalBannerAnalytics();
    }
    trackEvent('Page Change - documents');

    const localDocumentsCopy = documents.documents?.payload ? [...documents.documents.payload] : [];
    const myDocuments = setupDocuments(localDocumentsCopy);

    setSplitDocuments(myDocuments);
  }, []);

  const displayPersonalDetailsContent = () => {
    if (customerHasError || policyHasError || pageModel?.error) {
      return <InformationMessage text={DefaultText.APIError} />;
    }

    return (
      <ComponentView errorText={errorText} onErrorModalClose={() => { setErrorText(undefined); }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <PageTitle text="Membership documents" />
          {enableCarInsuranceWeb && hasCarInsurancePolicy && <InfoBlock content={<DocumentsInfoBlockContent />} />}
        </div>
        {visitedRenewalPage && (
          <OrangeInfoBanner
            title="Your latest renewal document"
            text="If you're looking for your renewal document and can't currently see it, please refresh the page and try again. We'll also update you by email when it's ready."
          />
        )}
        <div className="myrac-docs-content row my-3">
          <div className="col-sm-9 col-12 float-left">
            {splitDocuments?.currentDocuments && (<DocumentsListing documents={splitDocuments?.currentDocuments} downloadLinkTitle={pageModel?.documentCMS?.downloadlinktext} documentsIcon={pageModel?.documentCMS?.documentIcon?.icon} documentStatus="current" />)}
            {splitDocuments?.olderDocuments.length > 0 && (<DocumentsListing documents={splitDocuments?.olderDocuments} downloadLinkTitle={pageModel?.documentCMS?.downloadlinktext} documentsIcon={pageModel?.documentCMS?.documentIcon?.icon} documentStatus="older" />)}
          </div>
          <div className="col-sm-3 col-12 float-left">
            {renewBannerShown && renewBannerImgUrl
              ? (
                <RenderLink url={RENEWAL} analytics={adobeTag}>
                  <img src={renewBannerImgUrl} width="100%" alt="Renew Now" />
                </RenderLink>
              )
              : (<AbstractGrid columns={1} tiles={documentTiles} />)}
          </div>
        </div>
        <AdvertorialPanel advertorial={pageModel?.advertorial} />
      </ComponentView>
    );
  };

  return ((appState?.loading.status && !pageModel) ? null : displayPersonalDetailsContent());
};

const DocumentsInfoBlockContent = () => (
  <p>You can view your breakdown membership documents here. To access your car insurance documents, please visit the <Link to={RouteConstants.CAR_INSURANCE}>car insurance page</Link>.</p>
)

export default MembershipDocuments;
